import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, ChevronRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { ROUTE_PATHS } from "@/lib/index";
import { springPresets } from "@/lib/motion";

const LAST_UPDATED = "16 kwietnia 2026 r.";
const ADMIN_NAME   = "e-grób sp. z o.o. (w organizacji)";
const ADMIN_EMAIL  = "rodo@e-grob.pl";
const ADMIN_ADDR   = "ul. Przykładowa 1, 00-001 Warszawa, Polska";

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <motion.section id={id}
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={springPresets.gentle}
      className="mb-10">
      <h2 className="text-lg font-semibold mb-4 pb-2"
        style={{ fontFamily: "'Playfair Display', serif", color: "rgba(212,175,55,0.75)", borderBottom: "1px solid rgba(212,175,55,0.12)" }}>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed" style={{ color: "rgba(165,180,200,0.8)" }}>
        {children}
      </div>
    </motion.section>
  );
}

function Li({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <ChevronRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "rgba(212,175,55,0.4)" }} />
      <span>{children}</span>
    </li>
  );
}

export default function PrivacyPage() {
  return (
    <Layout>
      <div className="min-h-screen py-24 px-4"
        style={{ background: "linear-gradient(180deg, oklch(0.09 0.015 235) 0%, oklch(0.11 0.025 225) 100%)" }}>
        <div className="container mx-auto max-w-2xl">

          {/* Nagłówek */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle} className="mb-12 text-center">
            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.2)" }}>
                <Shield className="w-6 h-6" style={{ color: "rgba(212,175,55,0.7)" }} />
              </div>
            </div>
            <p className="text-xs uppercase tracking-[0.3em] mb-2" style={{ color: "rgba(140,155,175,0.5)" }}>
              Dokument prawny
            </p>
            <h1 className="text-3xl font-bold mb-3"
              style={{ fontFamily: "'Playfair Display', serif", color: "rgba(212,175,55,0.78)" }}>
              Polityka prywatności
            </h1>
            <p className="text-xs" style={{ color: "rgba(120,135,155,0.55)" }}>
              Ostatnia aktualizacja: {LAST_UPDATED}
            </p>
            <div className="mt-4 p-4 rounded-xl text-xs text-left"
              style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.15)" }}>
              <p style={{ color: "rgba(165,180,200,0.75)" }}>
                Niniejsza Polityka prywatności opisuje zasady przetwarzania danych osobowych przez platformę
                <strong style={{ color: "rgba(212,175,55,0.7)" }}> e-grób</strong> zgodnie z Rozporządzeniem Parlamentu
                Europejskiego i Rady (UE) 2016/679 (RODO) oraz polską ustawą o ochronie danych osobowych z dnia
                10 maja 2018 r. (Dz.U. 2018 poz. 1000).
              </p>
            </div>
          </motion.div>

          {/* § 1 */}
          <Section id="administrator" title="§ 1. Administrator danych osobowych">
            <p>Administratorem Twoich danych osobowych jest:</p>
            <div className="p-4 rounded-xl mt-2"
              style={{ background: "oklch(0.10 0.022 220)", border: "1px solid oklch(0.18 0.028 220)" }}>
              <p className="font-medium mb-1" style={{ color: "rgba(210,225,240,0.88)" }}>{ADMIN_NAME}</p>
              <p>{ADMIN_ADDR}</p>
              <p>E-mail: <a href={`mailto:${ADMIN_EMAIL}`} className="underline underline-offset-2 hover:opacity-80"
                style={{ color: "rgba(212,175,55,0.65)" }}>{ADMIN_EMAIL}</a></p>
            </div>
          </Section>

          {/* § 2 */}
          <Section id="podstawy" title="§ 2. Podstawy prawne i cele przetwarzania">
            <p>Przetwarzamy Twoje dane na następujących podstawach prawnych (art. 6 RODO):</p>
            <ul className="space-y-2 mt-2">
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Art. 6 ust. 1 lit. b RODO</strong> — wykonanie umowy (świadczenie usług platformy e-grób, w tym obsługa konta, przechowywanie treści e-grobu).</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Art. 6 ust. 1 lit. c RODO</strong> — wypełnienie obowiązku prawnego (m.in. przepisy podatkowe, rachunkowe).</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Art. 6 ust. 1 lit. f RODO</strong> — uzasadniony interes administratora (bezpieczeństwo systemu, zapobieganie nadużyciom, marketing bezpośredni do obecnych klientów).</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Art. 6 ust. 1 lit. a RODO</strong> — Twoja zgoda (np. newsletter, pliki cookie analityczne) — możesz ją wycofać w dowolnym momencie.</Li>
            </ul>
          </Section>

          {/* § 3 */}
          <Section id="dane" title="§ 3. Kategorie przetwarzanych danych">
            <p>Zbieramy wyłącznie dane niezbędne do działania usługi:</p>
            <ul className="space-y-2 mt-2">
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Dane identyfikacyjne:</strong> imię, nazwisko, adres e-mail.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Dane uwierzytelniające:</strong> zaszyfrowane hasło (bcrypt, zarządzane przez Supabase Auth).</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Treści e-grobu:</strong> zdjęcia, pliki, epitafia, elementy graficzne przesyłane dobrowolnie przez użytkownika.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Dane techniczne:</strong> adres IP, logi dostępu, typ urządzenia i przeglądarki (wyłącznie do celów bezpieczeństwa, przechowywane 30 dni).</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Dane transakcyjne:</strong> informacje o wybranym planie subskrypcji.</Li>
            </ul>
            <p className="mt-3 text-xs p-3 rounded-lg" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.12)" }}>
              <strong style={{ color: "rgba(212,175,55,0.7)" }}>Uwaga dotycząca danych szczególnych kategorii:</strong> Treści e-grobu mogą zawierać dane dotyczące zdrowia lub religii osób trzecich (zmarłych). Przesyłając je, wyrażasz zgodę na ich przetwarzanie wyłącznie w celu świadczenia usługi i zobowiązujesz się do posiadania stosownego umocowania (np. bycia osobą bliską).
            </p>
          </Section>

          {/* § 4 */}
          <Section id="czas" title="§ 4. Okres przechowywania danych">
            <ul className="space-y-2">
              <Li>Dane konta użytkownika — przez cały czas trwania umowy (aktywne konto) + 3 lata po jej rozwiązaniu (roszczenia cywilne).</Li>
              <Li>Treści e-grobu — przez czas aktywnego konta; po jego usunięciu trwałe usunięcie w ciągu 30 dni.</Li>
              <Li>Dane rozliczeniowe — 5 lat od końca roku podatkowego (art. 74 ustawy o rachunkowości).</Li>
              <Li>Logi systemowe — 30 dni.</Li>
              <Li>Dane przetwarzane na podstawie zgody — do jej wycofania.</Li>
            </ul>
          </Section>

          {/* § 5 */}
          <Section id="odbiorcy" title="§ 5. Odbiorcy danych — podmioty przetwarzające">
            <p>Twoje dane mogą być przekazywane wyłącznie:</p>
            <ul className="space-y-2 mt-2">
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Supabase Inc.</strong> (USA) — infrastruktura bazy danych i uwierzytelniania; transfer danych odbywa się na podstawie standardowych klauzul umownych (SCC) zgodnie z art. 46 RODO.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Operator płatności</strong> (w przyszłości) — wyłącznie niezbędne dane transakcyjne.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Organy publiczne</strong> — wyłącznie na podstawie prawnego obowiązku (np. Policja, sąd).</Li>
            </ul>
            <p className="mt-3">Nie sprzedajemy, nie wynajmujemy ani nie udostępniamy danych stronom trzecim w celach marketingowych.</p>
          </Section>

          {/* § 6 */}
          <Section id="prawa" title="§ 6. Twoje prawa (RODO)">
            <p>Na podstawie RODO przysługują Ci następujące prawa:</p>
            <ul className="space-y-2 mt-2">
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Prawo dostępu</strong> (art. 15) — uzyskanie kopii swoich danych.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Prawo do sprostowania</strong> (art. 16) — poprawienie błędnych danych.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Prawo do usunięcia</strong> (art. 17) — „prawo do bycia zapomnianym".</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Prawo do ograniczenia przetwarzania</strong> (art. 18).</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Prawo do przenoszenia danych</strong> (art. 20) — eksport danych w formacie JSON/CSV.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Prawo sprzeciwu</strong> (art. 21) — wobec przetwarzania na podstawie uzasadnionego interesu.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Prawo do skargi</strong> — do Prezesa Urzędu Ochrony Danych Osobowych (UODO), ul. Stawki 2, 00-193 Warszawa, <a href="https://uodo.gov.pl" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-80" style={{ color: "rgba(212,175,55,0.65)" }}>uodo.gov.pl</a>.</Li>
            </ul>
            <p className="mt-3">Aby skorzystać z praw, napisz na: <a href={`mailto:${ADMIN_EMAIL}`} className="underline underline-offset-2 hover:opacity-80" style={{ color: "rgba(212,175,55,0.65)" }}>{ADMIN_EMAIL}</a>. Odpowiemy w ciągu 30 dni.</p>
          </Section>

          {/* § 7 */}
          <Section id="cookies" title="§ 7. Pliki cookie">
            <p>Platforma wykorzystuje wyłącznie niezbędne pliki cookie do obsługi sesji uwierzytelniania (localStorage — token JWT Supabase). Nie stosujemy plików cookie analitycznych ani reklamowych bez Twojej zgody.</p>
          </Section>

          {/* § 8 */}
          <Section id="bezpieczenstwo" title="§ 8. Bezpieczeństwo danych">
            <ul className="space-y-2">
              <Li>Szyfrowanie TLS 1.3 dla całej komunikacji sieciowej.</Li>
              <Li>Hasła przechowywane w postaci skrótu bcrypt (zarządzane przez Supabase Auth).</Li>
              <Li>Row Level Security (RLS) w PostgreSQL — każdy użytkownik ma dostęp wyłącznie do własnych danych.</Li>
              <Li>Regularne kopie zapasowe bazy danych.</Li>
              <Li>Dostęp do danych produkcyjnych ograniczony do minimum niezbędnego personelu.</Li>
            </ul>
          </Section>

          {/* § 9 */}
          <Section id="zmiany" title="§ 9. Zmiany Polityki prywatności">
            <p>Zastrzegamy sobie prawo do zmiany niniejszej Polityki. O istotnych zmianach poinformujemy Cię e-mailem lub poprzez powiadomienie na platformie z co najmniej 14-dniowym wyprzedzeniem. Aktualna wersja jest zawsze dostępna pod adresem <strong style={{ color: "rgba(210,225,240,0.8)" }}>e-grob.pl/polityka-prywatnosci</strong>.</p>
          </Section>

          {/* Nawigacja do regulaminu */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to={ROUTE_PATHS.REGULAMIN}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all duration-200 hover:brightness-110"
              style={{ background: "rgba(212,175,55,0.85)", color: "rgba(6,8,18,0.95)" }}>
              Przejdź do Regulaminu <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to={ROUTE_PATHS.HOME}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm transition-all duration-200 hover:bg-white/5"
              style={{ border: "1px solid oklch(0.20 0.03 220)", color: "rgba(160,175,195,0.7)" }}>
              ← Wróć na stronę główną
            </Link>
          </div>

        </div>
      </div>
    </Layout>
  );
}
